import { isEmpty, none, prop, propEq, reject, mathMod } from 'ramda';
import { makeXPathEngineFromNamespaces } from '../../utility/xmlHelpers';
import { nsPc } from '@qdabra/forms-shared/xmlNamespaces';
import { selectNode, selectNodes } from '../qRules/helpers/qRules.utility';

import {
    KEY_DOWN,
    KEY_ENTER,
    KEY_ESCAPE,
    KEY_TAB,
    KEY_UP,
} from '../constants.keyboardKeys';

const $ = window.jQuery;

// class for items in the autocomplete dropdown
export const CLASS_PEOPLE_PICKER_DROPDOWN_ITEM = 'custom-picker-dropdown-item';
export const SELECTOR_PEOPLE_PICKER_DROPDOWN_ITEM = `.${CLASS_PEOPLE_PICKER_DROPDOWN_ITEM}`;

// class for the person "bubbles" in the people picker
const CLASS_SELECTION_ITEM = 'custom-picker-selection-item';
export const SELECTOR_SELECTION_ITEM = `.${CLASS_SELECTION_ITEM}`;

const PERSON_FIELD_XPATH_ENGINE = makeXPathEngineFromNamespaces({
    pc: nsPc,
});

const personNodeValue = (startNode, xpath) =>
    selectNode(PERSON_FIELD_XPATH_ENGINE, startNode, xpath)?.textContent || '';

// DOMNode -> Person
const personValuesFromPersonNode = (pNode) => {
    const displayName = personNodeValue(pNode, 'pc:DisplayName');
    const accountId = personNodeValue(pNode, 'pc:AccountId');
    const accountType = personNodeValue(pNode, 'pc:AccountType');

    return {
        id: accountId,
        text: displayName,
        DisplayName: displayName,
        AccountId: accountId,
        AccountType: accountType,
        EntityType: accountType,
        selected: true,
        disabled: false,
    };
};

// DataSourceNode -> [Person]
const getPersonValuesFromNode = (pickerNode) => {
    const personNodes = selectNodes(
        PERSON_FIELD_XPATH_ENGINE,
        pickerNode.getNode(),
        'pc:Person',
    );

    const data = personNodes
        .map(personValuesFromPersonNode)
        .filter((person) => !!person.AccountId);

    return isEmpty(data) ? null : data;
};

const getPersonValuesByNodeId = (dataSources, fvId, fvDs) => {
    if (!fvId) {
        return null;
    }

    const bindingNode = dataSources.getNodeById({ id: fvId, ds: fvDs });

    return bindingNode ? getPersonValuesFromNode(bindingNode) : null;
};

export const updateOriginalSelectForCustomPicker = ($select, selectedItems) => {
    $select.empty();

    for (const item of selectedItems) {
        const $option = $('<option>', {
            value: item.AccountId,
            text: item.DisplayName,
            selected: true,
        });
        $select.append($option);
    }
};

const updateCustomPickerPeopleData = ($pickerNode, people, allowMultiple) => {
    $pickerNode.data('selectedPpl', people);

    const accountIds = people.map(prop('AccountId'));

    const newValue = allowMultiple ? accountIds : accountIds[0] ?? '';

    $pickerNode.val(newValue).trigger('change');
};

export const addPeopleToCustomPicker = ($pickerNode, people, allowMultiple) => {
    if (!people.length) {
        return;
    }

    const selectedItemsData = $pickerNode.data('selectedPpl') || [];

    const beforeAddPeople = allowMultiple ? selectedItemsData : [];

    const addPeople = people.filter((user) =>
        none(propEq('AccountId', user.AccountId), beforeAddPeople),
    );

    const allPeople = [...beforeAddPeople, ...addPeople];

    updateCustomPickerPeopleData($pickerNode, allPeople, allowMultiple);
};

export const addSelectionItemToCustomPicker = (
    $container,
    user,
    $peoplePickerNode,
) => {
    const accountId = user.AccountId;

    const $item = $('<div>', {
        class: CLASS_SELECTION_ITEM,
        'data-id': accountId,
    });

    const $text = $('<span>', {
        class: 'selection-text',
        text: user.DisplayName,
    });

    const $remove = $('<span>', {
        class: 'selection-remove',
        text: '×',
    });

    $item.append($text).append($remove);

    $remove.on('click', (e) => {
        e.stopPropagation();
        e.preventDefault();

        const currentItems = $peoplePickerNode.data('selectedPpl') || [];

        const updatedItems = reject(
            (item) => item.AccountId === accountId,
            currentItems,
        );

        if (updatedItems.length !== currentItems.length) {
            $item.remove();
            const isMulti = $peoplePickerNode.prop('multiple');

            updateCustomPickerPeopleData(
                $peoplePickerNode,
                updatedItems,
                isMulti,
            );
        }
    });

    $container.find('.custom-picker-input').before($item);
};

export const getSelectionItems = ($dropdown) =>
    $dropdown.find(SELECTOR_PEOPLE_PICKER_DROPDOWN_ITEM);

let focusedItemIndex = -1;

export function updateFocusedItem(newValue, $dropdown) {
    const itemCount = getSelectionItems($dropdown).length;

    focusedItemIndex = itemCount > 0 ? newValue : -1;

    const $dropdownItems = $dropdown.find(SELECTOR_PEOPLE_PICKER_DROPDOWN_ITEM);

    $dropdownItems.removeClass('focused');

    if (focusedItemIndex >= 0 && $dropdownItems.length > focusedItemIndex) {
        $($dropdownItems[focusedItemIndex]).addClass('focused');
    }
}

function scrollToFocusedItem(direction, $dropdown) {
    if (focusedItemIndex >= 0) {
        const $focusedItem = $(
            $dropdown.find(SELECTOR_PEOPLE_PICKER_DROPDOWN_ITEM)[
                focusedItemIndex
            ],
        );
        const dropdownScroll = $dropdown.scrollTop();
        const dropdownHeight = $dropdown.height();
        const itemTop = $focusedItem.position().top + dropdownScroll;
        const itemHeight = $focusedItem.outerHeight();

        if (direction === 'up' && itemTop < dropdownScroll) {
            $dropdown.scrollTop(itemTop);
        } else if (
            direction === 'down' &&
            itemTop + itemHeight > dropdownScroll + dropdownHeight
        ) {
            $dropdown.scrollTop(itemTop + itemHeight - dropdownHeight);
        }
    }
}

export function closeDropdown($dropdown) {
    $dropdown.hide();
    updateFocusedItem(-1, $dropdown);
}

export function handleKeyboardNavigation(e, $dropdown) {
    if (!$dropdown.is(':visible')) {
        return;
    }

    const itemCount = getSelectionItems($dropdown).length;

    // Initialize focusedItemIndex to 0 if negative and there are items
    if (focusedItemIndex < 0 && itemCount > 0) {
        focusedItemIndex = 0;
    }

    switch (e.key) {
        case KEY_UP:
            e.preventDefault();
            updateFocusedItem(
                mathMod(focusedItemIndex - 1, itemCount),
                $dropdown,
            );
            scrollToFocusedItem('up', $dropdown);
            break;

        case KEY_DOWN:
            e.preventDefault();
            updateFocusedItem(
                mathMod(focusedItemIndex + 1, itemCount),
                $dropdown,
            );
            scrollToFocusedItem('down', $dropdown);
            break;

        case KEY_ENTER:
            e.preventDefault();
            if (focusedItemIndex >= 0 && focusedItemIndex < itemCount) {
                $(
                    $dropdown.find(SELECTOR_PEOPLE_PICKER_DROPDOWN_ITEM)[
                        focusedItemIndex
                    ],
                ).click();
            }
            break;

        case KEY_ESCAPE:
            e.preventDefault();
            closeDropdown($dropdown);
            break;

        case KEY_TAB:
            closeDropdown($dropdown);
            break;

        default:
            break;
    }
}

export default getPersonValuesByNodeId;
